import { useQuery } from '@tanstack/react-query';
import { userApi } from '../services/api';
import { useAuthStore } from '../store/authStore';

export type AppRole = 'creator' | 'poweruser' | 'member';

const ROLE_LEVELS: Record<AppRole, number> = {
  member: 0,
  poweruser: 1,
  creator: 2,
};

export function useRole() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const account = useAuthStore((s) => s.account);
  const role = useAuthStore((s) => s.role);
  const setRole = useAuthStore((s) => s.setRole);

  useQuery({
    queryKey: ['user-profile-role'],
    queryFn: async () => {
      const profile = await userApi.getProfile();
      const r = (profile.role as AppRole) || 'member';
      setRole(r);
      return r;
    },
    enabled: isAuthenticated && !role,
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
  });

  const currentRole: AppRole = role || 'member';

  // The signed-in address, and the Entra object ID behind it. Both are needed: a record written
  // through the hub holds an email in `createdBy`, one written by SkillBuilder holds the oid, and
  // the same person is behind both. Matching on email alone hid the Edit button from authors on
  // their own skills — the same mismatch the API used to refuse the write for.
  const email = account?.username?.toLowerCase() ?? null;
  const oid = account?.localAccountId?.toLowerCase() ?? null;

  return {
    role: currentRole,
    email,
    /**
     * Owner test. Takes the record when there is one, so both stored identities are considered;
     * a bare string still works for records that only ever carry the one field.
     */
    isAuthor: (subject?: string | null | { createdBy?: string; createdByOid?: string }) => {
      const claims = new Set([email, oid].filter(Boolean) as string[]);
      if (!claims.size || !subject) return false;
      const held = typeof subject === 'string'
        ? [subject]
        : [subject.createdBy, subject.createdByOid];
      return held.some((h) => !!h && claims.has(h.toLowerCase()));
    },
    hasRole: (required: AppRole) => ROLE_LEVELS[currentRole] >= ROLE_LEVELS[required],
    isCreator: currentRole === 'creator',
    isPowerUser: currentRole === 'poweruser' || currentRole === 'creator',
    isMember: true, // everyone is at least a member
  };
}
